import { useCallback, useEffect, useState } from 'react';

// Persistence layer for the dock's UI state — active tab and
// expanded/minimized. Chosen over router query params because the
// dock is visible on every /admin route: piggybacking on the URL
// would collide with route params and force effect-driven syncs on
// every navigation. localStorage keeps the persistence read/write
// inside the dock's own boundary.
const ACTIVE_TAB_STORAGE_KEY = 'admin_agent_dock:active_tab_id';
const IS_MINIMIZED_STORAGE_KEY = 'admin_agent_dock:is_minimized';

// Default the dock to minimized on first load — matches Linear's
// pattern: the persistent footer is the resting state until the
// user engages. Persisted so a reload restores whatever the user
// last chose.
const DEFAULT_IS_MINIMIZED = true;

// Three-state read result. `unavailable` means the read itself failed
// (no `window`, storage disabled/throwing) — the caller must NOT
// coerce that into `null`, since `null` is a legitimate stored value
// meaning "dock had no active chat". Rounds 6-8 kept regressing on the
// same symptom because the read collapsed these two states: a storage
// failure looked identical to an empty store, so the focus resync path
// happily wrote `null` into the live in-memory mirror and dropped the
// user's current chat selection.
type StorageReadResult =
    | { readonly status: 'ok'; readonly value: string | null }
    | { readonly status: 'unavailable' };

function readStoredActiveTabId(): StorageReadResult {
    if (typeof window === 'undefined') {
        return { status: 'unavailable' };
    }
    try {
        return {
            status: 'ok',
            value: window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY),
        };
    } catch {
        // Corporate browsers can strip storage under strict policies;
        // dock still works without persistence. Treated as
        // `unavailable` (NOT `ok` with `null`) so callers preserve the
        // live in-memory mirror instead of stomping it.
        return { status: 'unavailable' };
    }
}

// Synchronous mirror of the dock's live active-tab id. Row/card
// AddToChatButton affordances render OUTSIDE the dock tree and can't
// consume `useDockPersistence`, so they snapshot the active tab here
// at click time via `readCurrentAdminAgentChatId()` and bind that id
// into their ChipAddRequest. localStorage alone was insufficient
// (round-7 P1): a browser that throws on getItem (private mode,
// quota exceeded, corporate policy) reads back `null` and takes the
// chain-created-chat fallback in `applyChipAddRequest` — writing the
// chip to a fresh chat instead of the one the user could plainly see
// selected in the dock. `useDockPersistence` keeps this mirror in
// lockstep with its React state on mount, focus resync, and every
// `setActiveTabId` call. Once the dock has seeded the mirror at
// least once, it is authoritative — `null` genuinely means the dock
// has no active tab. Before any dock has mounted (e.g. bulk add on
// a route that renders the affordance before the dock ever
// initializes), the reader falls back to localStorage so the
// snapshot still tracks whatever the user had selected last
// session.
let currentActiveTabIdMirror: string | null = null;
let mirrorHasBeenSeeded = false;

function seedActiveTabIdMirror(chatId: string | null): void {
    currentActiveTabIdMirror = chatId;
    mirrorHasBeenSeeded = true;
}

function writeStoredActiveTabId(chatId: string | null): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        if (chatId === null) {
            window.localStorage.removeItem(ACTIVE_TAB_STORAGE_KEY);
        } else {
            window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, chatId);
        }
    } catch {
        // Silent — see above.
    }
}

type MinimizedReadResult =
    | { readonly status: 'ok'; readonly value: boolean }
    | { readonly status: 'unavailable' };

function readStoredIsMinimized(): MinimizedReadResult {
    if (typeof window === 'undefined') {
        return { status: 'unavailable' };
    }
    try {
        const raw = window.localStorage.getItem(IS_MINIMIZED_STORAGE_KEY);
        if (raw === null) {
            return { status: 'ok', value: DEFAULT_IS_MINIMIZED };
        }
        // Explicit string parse so a legacy value like '0'/'1' or
        // an accidental empty string can't quietly flip the dock
        // into the wrong state.
        return { status: 'ok', value: raw === 'true' };
    } catch {
        // Same rationale as `readStoredActiveTabId` — a thrown read
        // means "we do not know", not "default". Callers preserve
        // whatever state they already have instead of collapsing
        // the dock back to its default resting state (which round-8
        // did on focus resync).
        return { status: 'unavailable' };
    }
}

function writeStoredIsMinimized(isMinimized: boolean): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(
            IS_MINIMIZED_STORAGE_KEY,
            isMinimized ? 'true' : 'false',
        );
    } catch {
        // Silent — see above.
    }
}

export function useDockPersistence(): {
    readonly activeTabId: string | null;
    readonly setActiveTabId: (chatId: string | null) => void;
    readonly isMinimized: boolean;
    readonly setIsMinimized: (isMinimized: boolean) => void;
} {
    const [activeTabId, setActiveTabIdState] = useState<string | null>(() => {
        const result = readStoredActiveTabId();
        if (result.status === 'ok') {
            // Seed the module-level mirror synchronously so any
            // button click that races the very first render still
            // sees the dock's initial selection.
            seedActiveTabIdMirror(result.value);
            return result.value;
        }
        // Storage unavailable — leave the mirror unseeded so
        // `readCurrentAdminAgentChatId` does not falsely publish
        // `null` as an authoritative "no active chat". React state
        // still starts at `null`; the dock's list-reconcile effect
        // will hand a real chat back through `setActiveTabId`, which
        // seeds the mirror at that point.
        return null;
    });
    const [isMinimized, setIsMinimizedState] = useState<boolean>(() => {
        const result = readStoredIsMinimized();
        return result.status === 'ok' ? result.value : DEFAULT_IS_MINIMIZED;
    });

    // Re-sync from storage on window focus so a state change made in
    // another /admin tab surfaces immediately when the user returns.
    useEffect(() => {
        function handleFocus() {
            const storedTab = readStoredActiveTabId();
            if (storedTab.status === 'ok' && storedTab.value !== activeTabId) {
                seedActiveTabIdMirror(storedTab.value);
                setActiveTabIdState(storedTab.value);
            }
            // If storage is unavailable, preserve the live mirror and
            // React state — a throwing `getItem` is NOT a signal that
            // the dock has no active chat (round-8 P1 regressed on
            // exactly this collapse).
            const storedMinimized = readStoredIsMinimized();
            if (
                storedMinimized.status === 'ok' &&
                storedMinimized.value !== isMinimized
            ) {
                setIsMinimizedState(storedMinimized.value);
            }
            // Storage unavailable — preserve the current expanded/
            // minimized state instead of collapsing back to
            // `DEFAULT_IS_MINIMIZED` on every focus.
        }
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [activeTabId, isMinimized]);

    const setActiveTabId = useCallback((chatId: string | null) => {
        seedActiveTabIdMirror(chatId);
        setActiveTabIdState(chatId);
        writeStoredActiveTabId(chatId);
    }, []);

    const setIsMinimized = useCallback((next: boolean) => {
        setIsMinimizedState(next);
        writeStoredIsMinimized(next);
    }, []);

    return { activeTabId, setActiveTabId, isMinimized, setIsMinimized };
}

// Back-compat alias — old name for the same hook so any lingering
// import still works. The persistence layer now owns both the tab
// id and the minimized/expanded state.
export const useActiveTabPersistence = useDockPersistence;

// Synchronous read of the dock's current active-tab id. Row/card
// "+ add to chat" affordances live OUTSIDE the dock tree, so they
// can't consume `useDockPersistence` directly. They snapshot the
// active tab at click time via this reader and bind it into the
// chip-add request; the dock uses the bound id (not its own current
// activeTabIdRef) as the mutation target so a tab switch between
// click and drain never redirects the write (round-7 P1).
//
// The in-memory mirror maintained by `useDockPersistence` is the
// authoritative source once the dock has mounted at least once —
// `null` at that point genuinely means "no active tab" and drives
// the chain-created-chat fallback in `applyChipAddRequest`. Falling
// back to localStorage only when the dock has not yet seeded the
// mirror keeps the snapshot correct on the very first render, and
// keeps a private-mode / storage-blocked browser from silently
// redirecting a chip add to `currentChats[0]` (round-8 P1).
export function readCurrentAdminAgentChatId(): string | null {
    if (mirrorHasBeenSeeded) {
        return currentActiveTabIdMirror;
    }
    const result = readStoredActiveTabId();
    if (result.status === 'ok') {
        return result.value;
    }
    // Storage unavailable and no mount has seeded the mirror yet —
    // there is nothing authoritative to hand back. `null` here means
    // "unknown", and `applyChipAddRequest` treats it the same as an
    // empty dock (chain-created chat).
    return null;
}

export const ADMIN_AGENT_DOCK_ACTIVE_TAB_STORAGE_KEY = ACTIVE_TAB_STORAGE_KEY;
export const ADMIN_AGENT_DOCK_IS_MINIMIZED_STORAGE_KEY =
    IS_MINIMIZED_STORAGE_KEY;

// Test-only: reset the module-level mirror between tests so state
// from one test does not decide `readCurrentAdminAgentChatId`'s
// storage-fallback branch in another. Guarded by name so accidental
// production callers stand out.
export function _resetAdminAgentChatIdMirrorForTesting(): void {
    currentActiveTabIdMirror = null;
    mirrorHasBeenSeeded = false;
}
